import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore, IntakeField } from "@moss-js/founding-agent/core";
import { validateIntakeValues, postIntake } from "@moss-js/founding-agent/core";
import { icon } from "./icons.js";

export interface IntakePublisher {
  publishIntakeOpened(): void;
  publishIntakeCompleted(ev: { responses: Record<string, string> }): void;
}

export interface IntakeModalOpts {
  serviceUrl: string;
  publishableKey: string;
  publisher: IntakePublisher;
}

export interface IntakeModalHandle {
  destroy(): void;
}

const DEFAULT_TITLE = "Before we continue";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function getFocusables(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Inside a shadow root, document.activeElement reports the shadow host, not the
// focused control; read from the node's own root (falling back to the document
// when focus sits outside the shadow tree).
function activeElementIn(node: Node): Element | null {
  const rootNode = node.getRootNode();
  return rootNode instanceof ShadowRoot
    ? (rootNode.activeElement ?? document.activeElement)
    : document.activeElement;
}

export function createIntakeModal(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: IntakeModalOpts,
): IntakeModalHandle {
  let dialogEl: HTMLDivElement | null = null;
  let backdropEl: HTMLDivElement | null = null;
  let prevFocused: Element | null = null;

  // Local form values, submit state, and inline error -- mirrors the React
  // IntakeForm's useState; the core store carries no intake-submit fields.
  const formValues: Record<string, string> = {};
  let submitting = false;
  let error: string | null = null;

  // Bumped on every open edge and on a successful dismiss. A submit in flight
  // captures the generation before its POST; when the POST settles, a mismatch
  // (or the form no longer being open -- e.g. resolveIntake() was invoked by
  // some other path while the request was in flight) means this visitor already
  // left/reset the form, so the continuation must not publish intake_completed
  // or resolve the intake a second time.
  let generation = 0;

  // Set on destroy so a late-resolving fetch doesn't rebuild a detached modal.
  let disposed = false;

  // Body scroll-lock while the modal is open.
  let scrollLocked = false;
  let prevBodyOverflow = "";
  function lockScroll(): void {
    if (scrollLocked) return;
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    scrollLocked = true;
  }
  function unlockScroll(): void {
    if (!scrollLocked) return;
    document.body.style.overflow = prevBodyOverflow;
    scrollLocked = false;
  }

  // Dismiss (close button / Escape) just resolves the intake -- it does not
  // submit. The store restores the mic and marks intakeDone, so the trigger
  // never reopens this session. Unlike the booking modal, the backdrop is
  // deliberately NOT click-to-dismiss: an accidental click here would forfeit
  // the lead permanently (the form never reopens once resolved).
  //
  // Allowed even while a submit is in flight: resolveIntake() is idempotent
  // now (a no-op unless intakeOpen is still true), and the generation bump
  // plus the submit continuation's stale() check below mean a POST that
  // settles after this dismiss can't publish intake_completed or resolve a
  // form the visitor already left. A hung intake endpoint must not trap the
  // visitor behind an unresponsive close button.
  function dismiss(): void {
    generation++;
    store.getState().resolveIntake();
  }

  function trapFocus(e: KeyboardEvent): void {
    if (!dialogEl) return;
    const focusables = getFocusables(dialogEl);
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = activeElementIn(dialogEl);
    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    if (e.key === "Escape") {
      // Stop propagation so Escape does not also reach the global call keyboard
      // handler (bindKeyboard on window), which would end() the whole call.
      e.preventDefault();
      e.stopPropagation();
      dismiss();
    }
  }

  function handleSubmit(): void {
    if (submitting) return;
    const state = store.getState();
    const config = state.intakeConfig;
    if (!config || !state.bookingNonce) return;

    const result = validateIntakeValues(config.fields as IntakeField[], formValues);
    if (!result.ok) {
      error = result.error;
      rerender();
      return;
    }

    submitting = true;
    error = null;
    rerender();

    const nonce = state.bookingNonce;
    const gen = generation;
    // True once the visitor has left this exact open (dismissed, or a later
    // reopen) since the POST was fired, or the store has moved on to a
    // different session's nonce (reconnect) -- the continuation below must
    // treat the submit as abandoned rather than publish/resolve for a form
    // that isn't showing anymore, or complete a newer session with an old
    // submission's responses.
    const stale = () =>
      gen !== generation || !store.getState().intakeOpen || store.getState().bookingNonce !== nonce;

    void postIntake(opts.serviceUrl, opts.publishableKey, { nonce, responses: result.responses })
      .then(() => {
        // A stale continuation belongs to a form that is gone; the open-edge
        // reset already cleared submitting for any newer instance, so leave
        // that instance's state alone.
        if (disposed || stale()) return;
        submitting = false;
        opts.publisher.publishIntakeCompleted({ responses: result.responses });
        for (const k of Object.keys(formValues)) delete formValues[k];
        error = null;
        // Flips intakeOpen/intakeDone in the store, which fires the
        // subscription below and tears the dialog down -- no manual rerender().
        store.getState().resolveIntake();
      })
      .catch((err: unknown) => {
        if (disposed || stale()) return;
        submitting = false;
        error = err instanceof Error ? err.message : "Could not submit";
        rerender();
      });
  }

  function buildDialog(state: ReturnType<typeof store.getState>, shouldRender: boolean): void {
    if (backdropEl) {
      backdropEl.remove();
      backdropEl = null;
    }
    if (dialogEl) {
      dialogEl.remove();
      dialogEl = null;
    }
    document.removeEventListener("keydown", trapFocus);

    if (!shouldRender) {
      if (prevFocused && prevFocused instanceof HTMLElement) {
        prevFocused.focus();
        prevFocused = null;
      }
      return;
    }

    const config = state.intakeConfig!;
    const title = config.title ?? DEFAULT_TITLE;

    // Backdrop: deliberately not clickable (see dismiss() above).
    backdropEl = document.createElement("div");
    backdropEl.setAttribute("data-moss-intake-backdrop", "");
    backdropEl.setAttribute("aria-hidden", "true");
    root.appendChild(backdropEl);

    dialogEl = document.createElement("div");
    dialogEl.setAttribute("role", "dialog");
    dialogEl.setAttribute("aria-modal", "true");
    dialogEl.setAttribute("aria-label", title);
    dialogEl.setAttribute("data-moss-intake-modal", "");

    const headerEl = document.createElement("div");
    headerEl.setAttribute("data-moss-intake-header", "");

    const titleEl = document.createElement("span");
    titleEl.setAttribute("data-moss-intake-title", "");
    titleEl.textContent = title;
    headerEl.appendChild(titleEl);

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("data-moss-intake-close", "");
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.setAttribute("title", "Close");
    closeBtn.replaceChildren(icon("x", 14));
    closeBtn.addEventListener("click", dismiss);
    headerEl.appendChild(closeBtn);

    dialogEl.appendChild(headerEl);

    const bodyEl = document.createElement("div");
    bodyEl.setAttribute("data-moss-intake-body", "");

    const form = document.createElement("form");
    form.setAttribute("data-moss-intake-form", "");

    for (const f of config.fields as IntakeField[]) {
      const fieldEl = document.createElement("div");
      fieldEl.setAttribute("data-moss-intake-field", "");

      const labelEl = document.createElement("label");
      labelEl.setAttribute("for", "if-" + f.key);
      labelEl.textContent = f.label;
      if (f.required) {
        const star = document.createElement("span");
        star.setAttribute("data-moss-intake-required", "");
        star.textContent = "*";
        labelEl.appendChild(star);
      }
      fieldEl.appendChild(labelEl);

      const input = document.createElement("input");
      input.id = "if-" + f.key;
      if (f.type === "phone") {
        input.type = "tel";
        input.setAttribute("inputmode", "tel");
        input.setAttribute("autocomplete", "tel");
      } else if (f.type === "email") {
        input.type = "email";
        input.autocomplete = "email";
      } else {
        input.type = "text";
      }
      input.required = f.required;
      if (f.required) input.setAttribute("aria-required", "true");
      input.value = formValues[f.key] ?? "";
      input.addEventListener("input", () => {
        formValues[f.key] = input.value;
      });
      fieldEl.appendChild(input);
      form.appendChild(fieldEl);
    }

    if (error) {
      const errEl = document.createElement("div");
      errEl.setAttribute("data-moss-intake-error", "");
      errEl.setAttribute("role", "alert");
      errEl.setAttribute("aria-live", "assertive");
      const errText = document.createElement("p");
      errText.textContent = error;
      errEl.append(icon("alert-circle", 14), errText);
      form.appendChild(errEl);
    }

    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    submitBtn.setAttribute("data-moss-intake-submit", "");
    submitBtn.disabled = submitting;
    if (submitting) submitBtn.append(icon("loader-2"), "Submitting...");
    else submitBtn.textContent = error ? "Try Again" : "Continue";
    form.appendChild(submitBtn);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      handleSubmit();
    });

    bodyEl.appendChild(form);
    dialogEl.appendChild(bodyEl);

    const footerEl = document.createElement("div");
    footerEl.setAttribute("data-moss-intake-footer", "");
    const footerText = document.createElement("p");
    footerText.append("powered by ", Object.assign(document.createElement("span"), { textContent: "moss" }));
    footerEl.appendChild(footerText);
    dialogEl.appendChild(footerEl);
    root.appendChild(dialogEl);

    document.addEventListener("keydown", trapFocus);

    // Autofocus the first field (parity with the React form's autoFocus on
    // field index 0), not the header's close button.
    const firstField = dialogEl.querySelector<HTMLElement>(
      "[data-moss-intake-form] input, [data-moss-intake-form] textarea",
    );
    if (firstField) {
      firstField.focus();
    } else {
      const focusables = getFocusables(dialogEl);
      if (focusables.length > 0) {
        focusables[0]!.focus();
      } else {
        dialogEl.setAttribute("tabindex", "-1");
        dialogEl.focus();
      }
    }
  }

  let prevIntakeOpen = false;
  let prevIntakeConfig: ReturnType<typeof store.getState>["intakeConfig"] = null;
  let prevBookingNonce: string | null = null;
  let prevSubmitting = false;
  let prevError: string | null = null;
  let prevShouldRender = false;

  function rerender(): void {
    if (disposed) return;
    const state = store.getState();
    const shouldRender = state.intakeOpen && !!state.intakeConfig && !!state.bookingNonce;

    // Rebuild only when a structural input changed. The store subscription
    // fires on every write, and audio levels tick ~25Hz during a call; without
    // this gate buildDialog would tear the dialog down and rebuild it -- resetting
    // focus and losing typed input -- dozens of times a second. Local
    // submitting/error flags call rerender() directly, so they are part of
    // the signature too.
    const structuralChanged =
      state.intakeOpen !== prevIntakeOpen ||
      state.intakeConfig !== prevIntakeConfig ||
      state.bookingNonce !== prevBookingNonce ||
      submitting !== prevSubmitting ||
      error !== prevError;

    if (structuralChanged) {
      const openEdge = !prevShouldRender && shouldRender;
      if (openEdge) {
        // New generation: any submit continuation captured before this open
        // is now for a different (or the same, reopened) form instance and
        // must not act on this one. Also resets fresh form state per open,
        // and notifies the agent once per open.
        generation++;
        for (const k of Object.keys(formValues)) delete formValues[k];
        error = null;
        submitting = false;
        prevFocused = activeElementIn(root);
        lockScroll();
        opts.publisher.publishIntakeOpened();
      }
      if (prevShouldRender && !shouldRender) {
        unlockScroll();
      }

      buildDialog(state, shouldRender);
    }

    prevIntakeOpen = state.intakeOpen;
    prevIntakeConfig = state.intakeConfig;
    prevBookingNonce = state.bookingNonce;
    prevSubmitting = submitting;
    prevError = error;
    prevShouldRender = shouldRender;
  }

  // Initial render
  rerender();

  const unsub = store.subscribe(() => rerender());

  return {
    destroy() {
      disposed = true;
      unsub();
      document.removeEventListener("keydown", trapFocus);
      unlockScroll();
      backdropEl?.remove();
      backdropEl = null;
      dialogEl?.remove();
      dialogEl = null;
    },
  };
}
